import { Card, CardContent, CardHeader, Stack } from '@mui/material';
import { useMemo, useState } from 'react';
import { CpgReport, CpgRetailer } from '../../../api/schema';
import { formatters, ValueInput } from '@utils/formattersV2';
import {
  optimizationChartOptions,
  OptimizationChartOptionsProps,
} from '@features/campaigns/subdomains/visitation/configuration/optimizationChartOptions';
import { chartColors } from '@utils/unifiedPlatformColorPalette';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import {
  groupRetailers,
  MIN_RETAILER_COUNT,
  retailerLabel,
  sortRetailers,
} from '../../../domain/retailers';
import { CardTitle, MetricOption } from './CardTitle';
import { RetailersSelection } from './RetailersSelection';

export type MinimalCpgRetailer = Pick<
  CpgRetailer,
  | 'name'
  | 'projectedIncrementalSales'
  | 'incrementalSalesLift'
  | 'totalProjectedSales'
  | 'salesLiftIndex'
>;

type ChartProperties = {
  yAxisLabel: string;
  formatter: (value: ValueInput) => string;
  yAxisFormatter: (value: number) => string;
};

const chartProperties = {
  projectedIncrementalSales: {
    yAxisLabel: 'Projected Incremental Sales',
    formatter: value => formatters.currency(value, { full: true, decimals: 0 }),
    yAxisFormatter: formatters.currency,
  },
  incrementalSalesLift: {
    yAxisLabel: 'Incremental Sales Lift',
    formatter: value => formatters.lift(value, { decimals: 2 }),
    yAxisFormatter: formatters.percentage,
  },
  totalProjectedSales: {
    yAxisLabel: 'Total Projected Sales',
    formatter: value => formatters.currency(value, { full: true, decimals: 0 }),
    yAxisFormatter: formatters.currency,
  },
} as const satisfies Record<MetricOption['value'], ChartProperties>;

export type PerformanceOverTimeCardProps = {
  latestRetailers: MinimalCpgRetailer[];
  reports: (Pick<CpgReport, 'reportDate'> & {
    retailers: MinimalCpgRetailer[];
  })[];
};

export const PerformanceOverTimeCard = (
  props: PerformanceOverTimeCardProps
) => {
  const [selectedMetric, setSelectedMetric] = useState<MetricOption['value']>(
    'projectedIncrementalSales'
  );
  const latestRetailers: MinimalCpgRetailer[] = useMemo(() => {
    const groupedRetailers = groupRetailers(props.latestRetailers);
    return [
      ...groupedRetailers.topRetailers
        .toSorted(sortRetailers)
        .slice(0, MIN_RETAILER_COUNT),
      ...(groupedRetailers.otherRetailer
        ? [groupedRetailers.otherRetailer]
        : []),
    ];
  }, [props.latestRetailers]);

  const [selectedRetailers, setSelectedRetailers] = useState<
    Record<string, boolean>
  >(
    // eslint-disable-next-line unicorn/prefer-object-from-entries
    latestRetailers.reduce<Record<string, boolean>>(
      (acc, retailer) => ({
        ...acc,
        [retailerLabel(retailer.name)]: true,
      }),
      {}
    )
  );

  const chartProps: OptimizationChartOptionsProps = {
    yAxisLabel: chartProperties[selectedMetric].yAxisLabel,
    yAxisFormatter: chartProperties[selectedMetric].yAxisFormatter,
    valueFormatter: chartProperties[selectedMetric].formatter,
    dates: props.reports.map(report => report.reportDate),
    series: latestRetailers.map((retailer, retailerIndex) => {
      const displayName = retailerLabel(retailer.name);
      const data: OptimizationChartOptionsProps['series'][number]['data'] =
        props.reports.map(report => {
          const localRetailer = report.retailers.find(
            r => r.name === retailer.name
          );
          return {
            value: localRetailer?.[selectedMetric] ?? 0,
            date: report.reportDate,
          };
        });

      return {
        name: displayName,
        data,
        visible: selectedRetailers[displayName],
        color: chartColors[retailerIndex % chartColors.length],
      };
    }),
  };

  return (
    <Card>
      <CardHeader
        title={
          <CardTitle
            selectedMetric={selectedMetric}
            setSelectedMetric={setSelectedMetric}
          />
        }
      />

      <CardContent>
        <Stack direction="row" gap="1rem" alignItems="center" padding="1rem">
          <RetailersSelection
            items={chartProps.series}
            onToggleItem={name =>
              setSelectedRetailers({
                ...selectedRetailers,
                [name]: !selectedRetailers[name],
              })
            }
          />
          <HighchartsReact
            highcharts={Highcharts}
            options={optimizationChartOptions(chartProps)}
            containerProps={{
              ['data-testid']: 'performance-over-time-graph',
              style: {
                width: '100%',
              },
            }}
          />
        </Stack>
      </CardContent>
    </Card>
  );
};
